import { useState, useEffect, useRef } from "react";

// ─── module-level constants ─────────────────────────────────────────────
const SITES = ['DALLAS','FREDERICK','ASHBURN','PHOENIX','SALTLAKE','PORTLAND','CHICAGO'];
const SITE_LABELS = {
  DALLAS:'Dallas, TX', FREDERICK:'Frederick, MD', ASHBURN:'Ashburn, VA',
  PHOENIX:'Phoenix, AZ', SALTLAKE:'Salt Lake City, UT',
  PORTLAND:'Portland, OR', CHICAGO:'Chicago, IL'
};
const PHASE_MAP = {
  DALLAS:'A3', FREDERICK:'A3', ASHBURN:'A2',
  PHOENIX:'A2', SALTLAKE:'A1', PORTLAND:'A1', CHICAGO:'A3'
};

// ─── ParticleCanvas: module-level ───────────────────────────────────────
const ParticleCanvas = ({ color }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animating = true;
    let raf;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    const particles = Array.from({ length: 28 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.5 + 0.5,
      dx: (Math.random() - 0.5) * 0.4,
      dy: (Math.random() - 0.5) * 0.4,
      o: Math.random() * 0.5 + 0.1,
    }));
    const draw = () => {
      if (!animating) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        const alpha = Math.round(p.o * 255).toString(16).padStart(2, '0');
        ctx.fillStyle = color + alpha;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { animating = false; cancelAnimationFrame(raf); };
  }, [color]);
  return (
    <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
  );
};

// ─── ScanLines: module-level ────────────────────────────────────────────
const ScanLines = ({ color }) => {
  const id = 'scan-' + color.replace('#', '');
  return (
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.04 }}>
      <defs>
        <pattern id={id} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="4" stroke={color} strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
};

// ─── QuadrantCard: module-level ─────────────────────────────────────────
const QuadrantCard = ({ r, idx, onSelect }) => (
  <div
    onClick={() => onSelect(idx)}
    style={{
      position: 'relative', background: r.bg,
      border: `1px solid ${r.accent}22`,
      cursor: 'pointer', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between',
      padding: '40px 44px 36px',
      transition: 'border-color 0.3s',
      animation: `fadeSlideIn 0.6s ${idx * 0.12}s both`,
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = r.accent + '66'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = r.accent + '22'; }}
  >
    <ParticleCanvas color={r.accent} />
    <ScanLines color={r.accent} />

    <div style={{ position: 'relative', zIndex: 1 }}>
      <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '10px', letterSpacing: '0.25em', color: r.accent, marginBottom: '6px', textTransform: 'uppercase' }}>
        0{idx + 1} — {r.sub}
      </div>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '42px', fontWeight: 700, lineHeight: 1.0, color: '#E8EAF2', whiteSpace: 'pre-line', letterSpacing: '-0.01em' }}>
        {r.name}
      </div>
    </div>

    <div style={{ position: 'relative', zIndex: 1, margin: 'auto 0' }}>
      <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '88px', fontWeight: 700, color: r.accent, lineHeight: 1, letterSpacing: '-0.04em' }}>
        {r.metric}
        <span style={{ fontSize: '22px', opacity: 0.6 }}>{r.metricUnit}</span>
      </div>
      <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 300, color: '#6B7280', letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: '6px' }}>
        {r.metricLabel}
      </div>
    </div>

    <div style={{ position: 'relative', zIndex: 1, borderTop: `1px solid ${r.accent}22`, paddingTop: '18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {r.body.map(row => (
        <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
          <span style={{ color: '#4A5068' }}>{row.label}</span>
          <span style={{ color: '#9CA3AF', fontFamily: 'Space Mono, monospace', fontSize: '11px' }}>{row.val}</span>
        </div>
      ))}
    </div>

    <div style={{ position: 'absolute', bottom: '14px', right: '18px', fontFamily: 'Space Mono, monospace', fontSize: '9px', color: r.accent + '66', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
      Expand
    </div>
  </div>
);

// ─── ExpandedView: module-level ─────────────────────────────────────────
const ExpandedView = ({ r, signals, ferc, onClose }) => {
  const relatedSignals = signals.filter(s => {
    const t = (s.sector || s.theme || '').toLowerCase();
    return t === r.signalTheme || t.includes(r.signalTheme.replace('_', ''));
  }).slice(0, 3);
  const fercItems = ferc.slice(0, 3);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200, background: r.bg,
      display: 'flex', flexDirection: 'column',
      animation: 'expandIn 0.35s cubic-bezier(0.16,1,0.3,1) both',
      overflow: 'auto',
    }}>
      <ParticleCanvas color={r.accent} />
      <ScanLines color={r.accent} />

      <div style={{
        position: 'relative', zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '24px 48px', borderBottom: `1px solid ${r.accent}22`,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '20px' }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '28px', fontWeight: 700, color: '#E8EAF2' }}>
            {r.name.replace('\n', ' ')}
          </span>
          <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '10px', color: r.accent, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            {r.sub}
          </span>
        </div>
        <button onClick={onClose} style={{
          background: 'transparent', border: `1px solid ${r.accent}44`, color: r.accent,
          fontFamily: 'Space Mono, monospace', fontSize: '11px', padding: '8px 20px',
          cursor: 'pointer', letterSpacing: '0.15em',
        }}>
          BACK
        </button>
      </div>

      <div style={{
        position: 'relative', zIndex: 10, flex: 1,
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
        gap: '1px', background: r.accent + '11',
        margin: '40px 48px', border: `1px solid ${r.accent}22`,
      }}>
        <div style={{ background: r.bg, padding: '40px' }}>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '9px', color: r.accent, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '24px' }}>
            Primary Signal
          </div>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '96px', fontWeight: 700, color: r.accent, lineHeight: 1, letterSpacing: '-0.05em', marginBottom: '8px' }}>
            {r.metric}
            <span style={{ fontSize: '28px', opacity: 0.5 }}>{r.metricUnit}</span>
          </div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 300, color: '#6B7280', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '32px' }}>
            {r.metricLabel}
          </div>
          <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '15px', fontStyle: 'italic', color: '#9CA3AF', lineHeight: 1.7, maxWidth: '320px' }}>
            {r.insight}
          </p>
        </div>

        <div style={{ background: r.bg, padding: '40px' }}>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '9px', color: r.accent, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '24px' }}>
            Intelligence Breakdown
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {r.body.map(row => (
              <div key={row.label}>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#4A5068', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>
                  {row.label}
                </div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '22px', color: '#E8EAF2', fontWeight: 700 }}>
                  {row.val}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: r.bg, padding: '40px' }}>
          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '9px', color: r.accent, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '24px' }}>
            Market Signals
          </div>
          {relatedSignals.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {relatedSignals.map(s => (
                <div key={s.ticker} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  paddingBottom: '14px', borderBottom: `1px solid ${r.accent}11`,
                }}>
                  <div>
                    <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '14px', fontWeight: 700, color: '#E8EAF2' }}>{s.ticker}</div>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', color: '#4A5068', marginTop: '2px' }}>{s.company_name || '\u2014'}</div>
                  </div>
                  <div style={{
                    fontFamily: 'Space Mono, monospace', fontSize: '18px', fontWeight: 700,
                    color: (s.final_score || 0) >= 70 ? '#00D4B4' : (s.final_score || 0) >= 40 ? '#F0B429' : '#FF6B35',
                  }}>
                    {(s.final_score || 0).toFixed(0)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#4A5068', fontStyle: 'italic' }}>
              Signal data loading...
            </p>
          )}
          {r.id === 3 && fercItems.length > 0 && (
            <div style={{ marginTop: '28px' }}>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '9px', color: r.accent, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '14px' }}>
                Latest FERC Filings
              </div>
              {fercItems.map((f, i) => (
                <div key={i} style={{ paddingBottom: '10px', marginBottom: '10px', borderBottom: `1px solid ${r.accent}11` }}>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#9CA3AF' }}>
                    {f.docket_number || f.applicant_resolved || 'Filing'}
                  </div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '10px', color: r.accent + '88', marginTop: '3px' }}>
                    {f.filing_type || 'ISA'} {f.capacity_mw ? '\u00b7 ' + f.capacity_mw + 'MW' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── FourRivers: main component ─────────────────────────────────────────
export default function FourRivers() {
  const [site, setSite] = useState('DALLAS');
  const [active, setActive] = useState(null);
  const [scores, setScores] = useState(null);
  const [signals, setSignals] = useState([]);
  const [ferc, setFerc] = useState([]);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Space+Mono:wght@400;700&family=Inter:wght@300;400;500&display=swap';
    document.head.appendChild(l);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const api = process.env.REACT_APP_API_URL;

    fetch(`${api}/admin/ingest/corr004/results`)
      .then(r => r.json())
      .then(d => {
        const rows = (d.results || d || []).filter(r => r.edge_node_id === site);
        const byCorr = {};
        rows.forEach(r => { byCorr[r.correlator] = r; });
        setScores(byCorr);
      }).catch(() => {});

    fetch(`${api}/signals/scores`)
      .then(r => r.json())
      .then(d => setSignals(Array.isArray(d) ? d.slice(0, 8) : []))
      .catch(() => {});

    fetch(`${api}/ferc/events`)
      .then(r => r.json())
      .then(d => setFerc(Array.isArray(d) ? d.slice(0, 5) : []))
      .catch(() => {});
  }, [site]);

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') setActive(null); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const queueScore = scores?.queue_delay?.score ?? '--';
  const siteRisk   = scores?.site_risk?.score ?? '--';
  const procRisk   = scores?.procurement_risk?.score ?? '--';
  const phase      = PHASE_MAP[site] || 'A1';

  const rivers = [
    {
      id: 0, name: 'Value\nMonetization', sub: 'Order-to-Cash',
      accent: '#F0B429', bg: '#0D0A00',
      metric: typeof queueScore === 'number' ? (100 - queueScore).toFixed(0) : '--',
      metricLabel: 'Queue Headroom', metricUnit: '/100',
      body: [
        { label: 'Queue Delay Risk', val: queueScore + '/100' },
        { label: 'ISO Region', val: site === 'DALLAS' ? 'ERCOT' : site === 'CHICAGO' ? 'MISO' : 'PJM' },
        { label: 'Status', val: 'Tenant LOI Stage' },
      ],
      insight: 'Lower queue pressure means faster interconnection and earlier revenue commencement. Markets with <50% withdrawal rates historically close 18 months faster.',
      signalTheme: 'dc_reit',
    },
    {
      id: 1, name: 'Value\nCreation', sub: 'Idea-to-Delivery',
      accent: '#00D4B4', bg: '#00100D',
      metric: phase, metricLabel: 'Active Phase', metricUnit: '',
      body: [
        { label: 'Phase', val: phase + ' Construction' },
        { label: 'Procurement Risk', val: procRisk + '/100' },
        { label: 'Activity Count', val: '20 tracked' },
      ],
      insight: 'Construction phase carries the highest schedule risk exposure. Long-lead procurement windows and utility queue timing are the two variables with largest cost impact.',
      signalTheme: 'power_equip',
    },
    {
      id: 2, name: 'Capability', sub: 'Asset & People',
      accent: '#FF6B35', bg: '#100500',
      metric: typeof siteRisk === 'number' ? siteRisk.toFixed(1) : '--',
      metricLabel: 'Site Risk Score', metricUnit: '/100',
      body: [
        { label: 'Climate Exposure', val: '35% weight' },
        { label: 'Grid Capacity', val: '40% weight' },
        { label: 'Labor Pool', val: '25% weight' },
      ],
      insight: 'Site risk is a composite of NOAA climate data, EIA grid capacity, and BLS labor statistics. Scores below 20 indicate optimal physical operating conditions.',
      signalTheme: 'utility',
    },
    {
      id: 3, name: 'Relationship', sub: 'Customer Lifecycle',
      accent: '#A78BFA', bg: '#060010',
      metric: ferc.length || '--', metricLabel: 'FERC Filings', metricUnit: ' this week',
      body: [
        { label: 'Active Dockets', val: ferc.length + ' tracked' },
        { label: 'ISO Interconnect', val: 'Queue active' },
        { label: 'AHJ Status', val: 'Entitlement phase' },
      ],
      insight: 'FERC ISA execution events signal active interconnection progress. Cross-reference with ISO queue withdrawal rates to distinguish real capacity from paper megawatts.',
      signalTheme: 'neocloud',
    },
  ];

  return (
    <>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity:0; transform:translateY(16px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes expandIn {
          from { opacity:0; transform:scale(0.97); }
          to   { opacity:1; transform:scale(1); }
        }
      `}</style>

      <div style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: '#08090E',
        display: 'flex', flexDirection: 'column',
        fontFamily: 'Inter, sans-serif',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 40px', height: '56px',
          borderBottom: '1px solid #1C1F2E', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontSize: '18px', color: '#E8EAF2', letterSpacing: '0.02em' }}>
              Axiom
            </div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '8px', color: '#3B4058', letterSpacing: '0.3em', textTransform: 'uppercase', marginTop: '-2px' }}>
              CEO Flow
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {SITES.map(s => (
              <button key={s} onClick={() => setSite(s)} style={{
                background: s === site ? '#1C1F2E' : 'transparent',
                border: `1px solid ${s === site ? '#3B4058' : 'transparent'}`,
                color: s === site ? '#E8EAF2' : '#4A5068',
                fontFamily: 'Space Mono, monospace', fontSize: '9px',
                padding: '5px 12px', cursor: 'pointer',
                letterSpacing: '0.1em', textTransform: 'uppercase',
                transition: 'all 0.2s',
              }}>
                {s}
              </button>
            ))}
          </div>

          <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '11px', color: '#3B4058' }}>
            {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        </div>

        <div style={{
          flex: 1, display: 'grid',
          gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr',
          gap: '1px', background: '#1C1F2E', overflow: 'hidden',
        }}>
          {rivers.map((r, i) => (
            <QuadrantCard key={r.id} r={r} idx={i} onSelect={setActive} />
          ))}
        </div>

        <div style={{
          height: '36px', borderTop: '1px solid #1C1F2E',
          display: 'flex', alignItems: 'center',
          padding: '0 40px', gap: '40px', flexShrink: 0,
        }}>
          {[
            ['SITE', SITE_LABELS[site]],
            ['QUEUE PRESSURE', queueScore + '/100'],
            ['SITE RISK', siteRisk + '/100'],
            ['PROCUREMENT', procRisk + '/100'],
            ['FERC FILINGS', ferc.length],
            ['SIGNALS', signals.length],
          ].map(([label, val]) => (
            <div key={label} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '8px', color: '#3B4058', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                {label}
              </span>
              <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '11px', color: '#9CA3AF' }}>
                {val}
              </span>
            </div>
          ))}
        </div>
      </div>

      {active !== null && (
        <ExpandedView
          r={rivers[active]}
          signals={signals}
          ferc={ferc}
          onClose={() => setActive(null)}
        />
      )}
    </>
  );
}
